import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { LogOut, Check, X, CheckCircle2, XCircle, Ban, Calendar as CalIcon, ExternalLink } from "lucide-react";

function fmt12(hhmm) {
  const [h, m] = hhmm.split(":").map(Number);
  const suffix = h < 12 ? "AM" : "PM";
  const h12 = h === 0 ? 12 : h > 12 ? h - 12 : h;
  return `${h12}:${m.toString().padStart(2, "0")} ${suffix}`;
}

const STATUS_STYLES = {
  pending: "border-[#B8935A] text-[#B8935A]",
  confirmed: "border-emerald-500 text-emerald-400",
  rejected: "border-red-500 text-red-400",
  cancelled: "border-zinc-500 text-zinc-400",
  completed: "border-blue-500 text-blue-400",
};

export default function Admin() {
  const navigate = useNavigate();
  const [user, setUser] = useState(null);
  const [checking, setChecking] = useState(true);
  const [tab, setTab] = useState("pending");
  const [appointments, setAppointments] = useState([]);
  const [stats, setStats] = useState({ pending: 0, confirmed: 0, today: 0, total: 0 });
  const [blockedList, setBlockedList] = useState([]);
  const [blockDate, setBlockDate] = useState("");
  const [blockTime, setBlockTime] = useState("");

  useEffect(() => {
    api.get("/auth/me").then((r) => {
      if (!r.data.is_admin) { toast.error("Not an admin"); doLogin(); return; }
      setUser(r.data); setChecking(false);
    }).catch(() => { setChecking(false); doLogin(); });
  }, []);

  const doLogin = () => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    const redirectUrl = window.location.origin + "/admin";
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  const load = async () => {
    const [a, s, b] = await Promise.all([
      api.get(`/admin/appointments${tab !== "all" ? `?status=${tab}` : ""}`),
      api.get("/admin/stats"),
      api.get("/admin/blocked"),
    ]);
    setAppointments(a.data.appointments);
    setStats(s.data);
    setBlockedList(b.data.blocked);
  };

  useEffect(() => { if (user) load(); }, [user, tab]);

  const updateStatus = async (id, status) => {
    try {
      await api.patch(`/admin/appointments/${id}`, { status });
      toast.success(`Marked ${status}`);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed");
    }
  };

  const openWaLink = async (id, action) => {
    try {
      const { data } = await api.get(`/admin/whatsapp-link/${id}?action=${action}`);
      window.open(data.url, "_blank");
    } catch {}
  };

  const logout = async () => {
    await api.post("/auth/logout");
    setUser(null);
    doLogin();
  };

  const addBlock = async () => {
    if (!blockDate) { toast.error("Pick a date"); return; }
    try {
      await api.post("/admin/blocked", { blocked_date: blockDate, blocked_time: blockTime || null });
      toast.success("Blocked");
      setBlockDate(""); setBlockTime("");
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed");
    }
  };

  const removeBlock = async (id) => {
    await api.delete(`/admin/blocked/${id}`);
    load();
  };

  if (checking) return <div className="min-h-screen bg-[#0B0B0B] text-[#F7F3EC] flex items-center justify-center"><p className="eyebrow">Checking session…</p></div>;
  if (!user) return null;

  return (
    <div className="min-h-screen bg-[#0B0B0B] text-[#F7F3EC]" style={{ fontFamily: "Inter, Manrope, sans-serif" }}>
      <header className="sticky top-0 z-40 bg-[#0B0B0B]/85 backdrop-blur-xl border-b border-[rgba(184,147,90,0.15)]">
        <div className="max-w-7xl mx-auto px-5 md:px-8 flex items-center justify-between h-16">
          <div className="flex items-center gap-3">
            <span className="serif text-lg tracking-[0.35em] text-[#F7F3EC]">NOIR</span>
            <span className="w-4 h-px bg-[#B8935A]" />
            <span className="serif text-lg tracking-[0.35em] text-[#F7F3EC]">NAILS</span>
            <span className="ml-4 text-xs tracking-[0.28em] uppercase text-[#A1A1AA]">Admin</span>
          </div>
          <div className="flex items-center gap-4">
            <span className="text-xs text-[#A1A1AA] hidden sm:inline">{user.email}</span>
            <button onClick={logout} data-testid="admin-logout" className="text-xs tracking-[0.28em] uppercase text-[#A1A1AA] hover:text-[#B8935A] inline-flex items-center gap-2"><LogOut size={14} /> Sign out</button>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-5 md:px-8 py-8">
        {/* Stats */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3 md:gap-4" data-testid="stats-grid">
          {[
            { label: "Today", val: stats.today },
            { label: "Pending", val: stats.pending },
            { label: "Confirmed", val: stats.confirmed },
            { label: "Total", val: stats.total },
          ].map((s) => (
            <div key={s.label} className="border border-[rgba(184,147,90,0.25)] p-5 bg-[#0E0E0E]">
              <p className="text-xs tracking-[0.28em] uppercase text-[#A1A1AA]">{s.label}</p>
              <p className="serif text-4xl mt-3">{s.val}</p>
            </div>
          ))}
        </div>

        {/* Tabs */}
        <div className="mt-10 flex flex-wrap items-center gap-2">
          {["pending", "confirmed", "completed", "rejected", "cancelled", "all"].map((t) => (
            <button
              key={t}
              onClick={() => setTab(t)}
              data-testid={`tab-${t}`}
              className={`text-xs tracking-[0.28em] uppercase px-4 py-2 border transition-colors ${
                tab === t ? "border-[#B8935A] text-[#B8935A]" : "border-[rgba(247,243,236,0.15)] text-[#A1A1AA] hover:text-[#F7F3EC]"
              }`}
            >
              {t}
            </button>
          ))}
        </div>

        {/* Appointments */}
        <div className="mt-6 space-y-3" data-testid="appointments-list">
          {appointments.length === 0 && <p className="text-[#A1A1AA] py-12 text-center">No appointments in this view.</p>}
          {appointments.map((a) => (
            <div key={a.id} className="border border-[rgba(247,243,236,0.1)] bg-[#0E0E0E] p-5 flex flex-col lg:flex-row gap-4 lg:items-center" data-testid={`appt-row-${a.reference}`}>
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-3 flex-wrap">
                  <p className="serif text-xl">{a.customer_name}</p>
                  <span className={`text-[10px] tracking-[0.2em] uppercase border px-2 py-1 ${STATUS_STYLES[a.status]}`}>{a.status}</span>
                  <span className="text-xs text-[#A1A1AA]">{a.reference}</span>
                </div>
                <p className="text-sm text-[#A1A1AA] mt-2">
                  {a.appointment_date} · {fmt12(a.appointment_time)} · <a href={`https://wa.me/${a.mobile.replace(/\D/g, "")}`} target="_blank" rel="noreferrer" className="text-[#B8935A] hover:underline">{a.mobile}</a>
                </p>
                <p className="text-sm mt-2 text-[#F7F3EC]">{a.services.join(" · ")}</p>
                {a.note && <p className="text-xs text-[#A1A1AA] italic mt-2">"{a.note}"</p>}
              </div>
              <div className="flex flex-wrap gap-2 shrink-0">
                {a.status === "pending" && (
                  <>
                    <button onClick={() => updateStatus(a.id, "confirmed")} data-testid={`approve-${a.reference}`} className="text-xs uppercase tracking-[0.2em] px-3 py-2 bg-emerald-500/10 text-emerald-400 border border-emerald-500/50 hover:bg-emerald-500 hover:text-[#0B0B0B] inline-flex items-center gap-1"><Check size={12} /> Approve</button>
                    <button onClick={() => updateStatus(a.id, "rejected")} data-testid={`reject-${a.reference}`} className="text-xs uppercase tracking-[0.2em] px-3 py-2 bg-red-500/10 text-red-400 border border-red-500/50 hover:bg-red-500 hover:text-[#0B0B0B] inline-flex items-center gap-1"><X size={12} /> Reject</button>
                  </>
                )}
                {a.status === "confirmed" && (
                  <>
                    <button onClick={() => updateStatus(a.id, "completed")} data-testid={`complete-${a.reference}`} className="text-xs uppercase tracking-[0.2em] px-3 py-2 bg-blue-500/10 text-blue-400 border border-blue-500/50 hover:bg-blue-500 hover:text-[#0B0B0B] inline-flex items-center gap-1"><CheckCircle2 size={12} /> Mark Done</button>
                    <button onClick={() => updateStatus(a.id, "cancelled")} data-testid={`cancel-${a.reference}`} className="text-xs uppercase tracking-[0.2em] px-3 py-2 bg-zinc-500/10 text-zinc-400 border border-zinc-500/50 hover:bg-zinc-500 hover:text-[#0B0B0B] inline-flex items-center gap-1"><Ban size={12} /> Cancel</button>
                  </>
                )}
                <button onClick={() => openWaLink(a.id, a.status === "rejected" ? "reject" : "confirm")} className="text-xs uppercase tracking-[0.2em] px-3 py-2 border border-[#B8935A] text-[#B8935A] hover:bg-[#B8935A] hover:text-[#0B0B0B] inline-flex items-center gap-1" data-testid={`wa-${a.reference}`}><ExternalLink size={12} /> WhatsApp</button>
              </div>
            </div>
          ))}
        </div>

        {/* Blocked slots manager */}
        <section className="mt-16 border-t border-[rgba(184,147,90,0.2)] pt-10">
          <div className="flex items-center gap-3 mb-6">
            <CalIcon size={16} className="text-[#B8935A]" />
            <h2 className="serif text-2xl">Blocked Dates & Times</h2>
          </div>
          <div className="grid md:grid-cols-3 gap-4 items-end">
            <div>
              <label className="eyebrow block mb-2">Date</label>
              <input type="date" value={blockDate} onChange={(e) => setBlockDate(e.target.value)} data-testid="block-date" className="w-full bg-[#0E0E0E] border border-[rgba(247,243,236,0.15)] p-3 text-sm" />
            </div>
            <div>
              <label className="eyebrow block mb-2">Time (leave empty to block full day)</label>
              <input type="time" step="1800" value={blockTime} onChange={(e) => setBlockTime(e.target.value)} data-testid="block-time" className="w-full bg-[#0E0E0E] border border-[rgba(247,243,236,0.15)] p-3 text-sm" />
            </div>
            <button onClick={addBlock} data-testid="block-add" className="btn-gold">Block Slot</button>
          </div>
          <div className="mt-6 grid sm:grid-cols-2 md:grid-cols-3 gap-3">
            {blockedList.map((b) => (
              <div key={b.id} className="border border-[rgba(247,243,236,0.1)] p-4 flex items-center justify-between" data-testid={`blocked-${b.id}`}>
                <div>
                  <p className="text-sm">{b.blocked_date}{b.blocked_time ? ` · ${fmt12(b.blocked_time)}` : " · Full day"}</p>
                  {b.reason && <p className="text-xs text-[#A1A1AA] mt-1">{b.reason}</p>}
                </div>
                <button onClick={() => removeBlock(b.id)} data-testid={`unblock-${b.id}`} className="text-xs uppercase tracking-[0.2em] text-red-400 hover:text-red-300"><XCircle size={16} /></button>
              </div>
            ))}
            {blockedList.length === 0 && <p className="text-sm text-[#A1A1AA]">No blocked slots.</p>}
          </div>
        </section>
      </main>
    </div>
  );
}
